/**
 * Course Structure Types (Normalized)
 * Requirements: 2.1
 */

import type { CourseBlock } from './blocks';

/**
 * Generic paginated list envelope returned by GET /lessons and GET /courses.
 */
export interface ListResponse<T> {
  items: T[];
  totalCount: number;
}

/**
 * Lightweight lesson summary embedded in course detail responses
 * (getCourse, createCourse, updateCourse). Never present in listCourses.
 */
export interface CourseLessonSummary {
  id: string;
  /** Raw auto-increment id, shown in the editor as the human-readable "L{n}". */
  numericId?: number;
  title: string;
  workflowState: WorkflowState;
  /**
   * The active context's lifecycle behind the pill: 'editing' renders as
   * "Published — editing" so a published lesson with pending edits is
   * distinguishable from a cleanly published one.
   */
  publishLifecycle?: PublishLifecycleState;
  estimatedTimeMinutes?: number;
  /**
   * Client-context provenance markers — present only on account-scoped
   * (client-context) editor reads. `courseLessonId` identifies the course-lesson
   * anchor row (needed to address suppress/reorder/delete on saves), base rows
   * carry a customizationState, and client-added rows carry clientAdded.
   */
  courseLessonId?: string;
  clientAdded?: boolean;
  customizationState?: CourseLessonCustomizationState;
}

export type CourseLessonCustomizationState = 'inherited' | 'suppressed';

/**
 * One entry of a client-context course composition save, in the account's
 * resolved display order (suppressed base rows included). `id` is the
 * courseLessonId for existing anchors, or a `tmp_*` id for a client add.
 */
export interface ClientContextCourseLessonEntry {
  id: string;
  /** Lesson slug (public id). */
  lessonId: string;
  customizationState?: CourseLessonCustomizationState;
  clientAdded?: boolean;
}

export type WorkflowState = 'draft' | 'published' | 'archived';

// NA-190 publish & export workflow: per-context lifecycle state. `draft` =
// never published; `locked` = Published (read-only); `editing` = Published —
// editing (unlocked, pending edits not yet live in the context).
export type PublishLifecycleState = 'draft' | 'locked' | 'editing';

export interface PublishState {
  state: PublishLifecycleState;
  lastPublishedAt: string | null;
  /** NA-221: a locked client context whose Emtrain version is mid-edit. */
  lockedByEmtrainEditing: boolean;
  editable: boolean;
  /** NA-296: true only while `state === 'editing'` and no edit has been saved yet. */
  canCancelEdit: boolean;
}

// NA-283: why a Delete control is disabled. Server-computed from the same
// predicates the DELETE endpoints enforce — never re-derive it from
// workflowState/assignedAccountIds/courseUsageCount, which are scoped
// differently and would advertise deletable rows that then 409.
export type DeleteBlockedReason = 'usedInCourses' | 'assignedToAccounts';

export interface DeleteEligibility {
  deletable: boolean;
  /** Present only when blocked. */
  reason?: DeleteBlockedReason;
  /** The gating predicate's own count, for the disabled-control copy. */
  blockingCount?: number;
}

export interface LocaleOption {
  locale: string;
  label: string;
}

export const DEFAULT_CONTENT_LOCALE = 'en';

/** Slot-keyed lesson color palette. Each slot holds a hex color. */
export interface LessonColors {
  color1: string;
  color2: string;
  color3: string;
  color4: string;
}

/**
 * A lesson stores its theme color as a palette slot, so it indexes the
 * account's LessonColors directly with no name → slot conversion.
 */
export type LessonThemeColor = keyof LessonColors;

/** Default hex values for each lesson color slot. */
export const LESSON_THEME_COLORS: Record<LessonThemeColor, string> = {
  color1: '#5B8EFF',
  color2: '#C70063',
  color3: '#FF7000',
  color4: '#40405D',
};

export const DEFAULT_LESSON_THEME_COLOR: LessonThemeColor = 'color1';

/**
 * Resolve the hex color for a lesson theme color slot against an account's
 * saved lesson palette, falling back to DEFAULT_LESSON_THEME_COLOR.
 *
 * @param name - The LessonThemeColor slot (e.g. 'color1'), or undefined to use DEFAULT_LESSON_THEME_COLOR.
 * @param lessonColors - The account's saved lesson color palette from theme tokens.
 */
export function resolveLessonThemeColor(
  name: LessonThemeColor | undefined,
  lessonColors: LessonColors,
): string {
  return lessonColors[name ?? DEFAULT_LESSON_THEME_COLOR];
}

export interface Course {
  id: string;
  /** Raw auto-increment id, shown in the editor as the human-readable "C{n}". */
  numericId?: number;
  title: string;
  description: string;
  /** Locale used to resolve the editable course metadata response. */
  locale?: string;
  /** Locale used as fallback when translated rows are incomplete. */
  defaultLocale?: string;
  /** Locales currently available for this course. */
  availableLocales?: LocaleOption[];
  /**
   * Ordered references to reusable lessons.
   * Courses do not own lesson content; they package lessons.
   */
  lessonIds: string[];
  /**
   * Lightweight per-lesson summaries for the course's lessons. Present in detail
   * responses (getCourse/createCourse/updateCourse), omitted from list responses.
   */
  lessons?: CourseLessonSummary[];
  /**
   * Optional: courses may later support their own workflow/versioning for packaging changes.
   * In the lesson-first model, runtime course content resolves to the latest published lessons.
   */
  workflowState?: WorkflowState;
  /** NA-190: the active context's publish state (detail responses). */
  publishState?: PublishState;
  /** List-response lifecycle nuance behind the pill ('editing' ⇒ "Published — editing"). */
  publishLifecycle?: PublishLifecycleState;
  /** NA-283: server-computed Delete availability (list + detail responses). */
  deleteEligibility?: DeleteEligibility;
  currentVersionId?: string;
  timedCourse?: boolean;
  durationMinutes?: number | null;
  minimumRequiredMinutes?: number | null;
  thumbnailSrc?: string;
  thumbnailAlt?: string;
  completionCertificateEnabled?: boolean;
  /** Certificate text shown on the completion certificate only. */
  certificateMessage?: string;
  /** End-of-course message shown on the course-completion screen. */
  completedMessage?: string;
  assignedAccountIds?: string[];
  customizedByAccountCount?: number;
  createdByUserId?: string | null;
  createdByName?: string;
  updatedByUserId?: string | null;
  updatedByName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Lesson {
  id: string;
  /** Raw auto-increment id, shown in the editor as the human-readable "L{n}". */
  numericId?: number;
  title: string;
  description: string;
  /** Locale used to resolve this lesson payload. */
  locale?: string;
  /** Locale used as fallback when translated rows are incomplete. */
  defaultLocale?: string;
  /** Locales currently available for this lesson. */
  availableLocales?: LocaleOption[];
  /** Locales whose effective learner-visible content is fully translated. */
  fullyTranslatedLocales?: string[];
  /** Optional author-provided estimate shown in navigation UI. */
  estimatedTimeMinutes?: number;
  /** Theme color for the lesson title underline. */
  themeColor?: LessonThemeColor;
  workflowState: WorkflowState;
  /** List-response lifecycle nuance behind the pill ('editing' ⇒ "Published — editing"). */
  publishLifecycle?: PublishLifecycleState;
  /** NA-190: the active context's publish state (detail responses). */
  publishState?: PublishState;
  currentVersionId?: string;
  assignedAccountIds?: string[];
  /** NA-284: distinct courses referencing this lesson (list responses). */
  courseUsageCount?: number;
  /** NA-283: server-computed Delete availability (list + detail responses). */
  deleteEligibility?: DeleteEligibility;
  createdAt: string;
  updatedAt: string;
  /** Ordered array of Block IDs directly in the lesson */
  blockOrder: string[];
}

export interface NarrationReadyEntry {
  status: 'ready';
  url: string;
}

export interface NarrationEmptyEntry {
  status: 'empty';
  url?: never;
}

export interface NarrationMissingEntry {
  status: 'missing';
  url?: never;
}

export type NarrationManifestEntry =
  | NarrationReadyEntry
  | NarrationEmptyEntry
  | NarrationMissingEntry;

export type NarrationBlockManifest = Record<string, NarrationManifestEntry>;

export const STATIC_NARRATION_PROMPT_KEYS = [
  'resume.after_video',
  'resume.after_document_acknowledgement',
  'resume.after_acknowledgement',
  'resume.on_other_option',
  'resume.on_region_selection',
  'resume.on_link_click',
  'pause.submit_and_review_results',
  'pause.submit_response',
  'pause.review_results',
] as const;

export type StaticNarrationPromptKey = (typeof STATIC_NARRATION_PROMPT_KEYS)[number];

/**
 * Prompt keys retired from the contract (NA-358). Manifests may still list them
 * (old packages, DB rows not yet cleaned up), so validation accepts them, but the
 * merge never keeps them and nothing plays them.
 */
export const RETIRED_STATIC_NARRATION_PROMPT_KEYS = [
  'resume.on_topic_selection',
  'resume.on_another_topic_selection',
] as const;

/** Derived learner-runtime data. It is never part of authored block payloads. */
export interface NarrationManifest {
  blocks: Record<string, NarrationBlockManifest>;
  lessonTitles?: Record<string, NarrationManifestEntry>;
  mapRegions?: Record<string, NarrationManifestEntry>;
  staticPrompts?: Partial<Record<StaticNarrationPromptKey, NarrationManifestEntry>>;
}

export const NARRATION_QUESTION_BLOCK_TYPES = [
  'likert-scale',
  'workplace-color-spectrum',
  'single-choice-answer',
  'categorization',
  'freeform-text',
] as const;

export const NARRATION_SUPPORTED_BLOCK_TYPES = [
  'basic-content-a',
  'stylized-numbered-list',
  'stylized-sidebar-callout',
  'stylized-text-callout',
  'lesson-complete',
  'video',
  'policy-acknowledgement',
  'electronic-signature',
  'map',
  'click-expand',
  'comparison-slider',
  'link',
  ...NARRATION_QUESTION_BLOCK_TYPES,
] as const;

const NARRATION_SUPPORTED_BLOCK_TYPE_SET = new Set<string>(NARRATION_SUPPORTED_BLOCK_TYPES);

export function isNarrationSupportedBlockType(type: string): boolean {
  return NARRATION_SUPPORTED_BLOCK_TYPE_SET.has(type);
}

export interface ExportedCourse {
  id: string;
  /** Raw auto-increment id (NA-408 block-id string's `C{n}` segment) — distinct from `id`, which is the opaque routing id. */
  numericId?: number;
  title: string;
  description: string;
  /** Locale used to resolve this payload. */
  locale?: string;
  /** Locale used as the fallback source when translated rows are missing. */
  defaultLocale?: string;
  /** Locales available in the exported package or preview source. */
  availableLocales?: LocaleOption[];
  lessons: Lesson[];
  blocks: CourseBlock[];
  /** Optional for backwards compatibility with packages built before narration. */
  narration?: NarrationManifest;
  /** Whether the course offers a downloadable completion certificate. */
  completionCertificateEnabled?: boolean;
  /** Certificate text shown on the completion certificate only. */
  certificateMessage?: string;
  /** End-of-course message shown on the course-completion screen. */
  completedMessage?: string;
  /** Whether this course has a timed requirement. */
  timedCourse?: boolean;
  /** Total duration in minutes when non timed. */
  durationMinutes?: number | null;
  /** Minimum required minutes the learner must spend when timed. */
  minimumRequiredMinutes?: number | null;
}

/**
 * Denormalized lesson snapshot used for lesson publishing/versioning.
 */
export type ExportedLesson = Lesson & {
  blocks: CourseBlock[];
};

/**
 * A single lesson reference inside a course-pointer package. `href` is a
 * root-relative path to that lesson's independently-hosted package within the
 * same account scope (e.g. `/courses/{lessonId}/current/`), so it resolves only
 * within the serving subdomain — preserving tenant isolation (referenced-lesson-packages).
 */
export interface LessonRef {
  id: string;
  href: string;
}

/**
 * The deployed course package's `data/course.json` in the referenced-lesson
 * model: course metadata + the account-resolved lesson sequence as references,
 * with NO embedded lesson content. The export player fetches each ref and
 * stitches them into an `ExportedCourse` before playback. Legacy embedded
 * packages omit `kind` (or use `'course'`) and carry `lessons`/`blocks` inline.
 */
export interface CoursePointer {
  kind: 'course-pointer';
  id: string;
  /** Raw auto-increment id (NA-408 block-id string's `C{n}` segment) — distinct from `id`, which is the opaque routing id. */
  numericId?: number;
  title: string;
  description: string;
  locale?: string;
  defaultLocale?: string;
  availableLocales?: LocaleOption[];
  lessonRefs: LessonRef[];
  completionCertificateEnabled?: boolean;
  /** Certificate text shown on the completion certificate only. */
  certificateMessage?: string;
  /** End-of-course message shown on the course-completion screen. */
  completedMessage?: string;
  timedCourse?: boolean;
  durationMinutes?: number | null;
  minimumRequiredMinutes?: number | null;
}

/** Localized metadata for one entry in a normalized course pointer bundle. */
export interface CoursePointerLocalizedMetadata {
  locale: string;
  title: string;
  description: string;
  certificateMessage: string;
  completedMessage: string;
}

/** Settings shared by every localized pointer for one course/account scope. */
export interface CoursePointerSettings {
  timedCourse: boolean;
  durationMinutes: number | null;
  minimumRequiredMinutes: number | null;
  completionCertificateEnabled: boolean;
}

/**
 * Complete normalized input for one course/account pointer bundle. Invariant
 * composition and settings are carried once; localized metadata follows the
 * same default-first order as `availableLocales`.
 */
export interface CoursePointerBundle {
  kind: 'course-pointer-bundle';
  id: string;
  /** Raw auto-increment id (NA-408 block-id string's `C{n}` segment). */
  numericId?: number;
  defaultLocale: string;
  availableLocales: LocaleOption[];
  lessonRefs: LessonRef[];
  settings: CoursePointerSettings;
  localizedMetadata: CoursePointerLocalizedMetadata[];
}

/** Reject incomplete pointer bundles before AuthorAI expands any pointers. */
export function isCoursePointerBundle(data: unknown): data is CoursePointerBundle {
  if (!data || typeof data !== 'object') return false;
  const bundle = data as Partial<CoursePointerBundle>;
  if (
    bundle.kind !== 'course-pointer-bundle' ||
    typeof bundle.id !== 'string' ||
    typeof bundle.defaultLocale !== 'string' ||
    !Array.isArray(bundle.availableLocales) ||
    !Array.isArray(bundle.lessonRefs) ||
    !Array.isArray(bundle.localizedMetadata) ||
    !bundle.settings ||
    typeof bundle.settings !== 'object'
  ) return false;

  const locales = bundle.availableLocales.map((option) => option?.locale);
  if (
    locales.length === 0 ||
    locales[0] !== bundle.defaultLocale ||
    new Set(locales).size !== locales.length ||
    bundle.localizedMetadata.length !== locales.length
  ) return false;

  const settings = bundle.settings;
  const isNullableNumber = (value: unknown): value is number | null =>
    value === null || typeof value === 'number';
  if (
    typeof settings.timedCourse !== 'boolean' ||
    !isNullableNumber(settings.durationMinutes) ||
    !isNullableNumber(settings.minimumRequiredMinutes) ||
    typeof settings.completionCertificateEnabled !== 'boolean'
  ) return false;

  if (!bundle.availableLocales.every((option) =>
    option && typeof option.locale === 'string' && typeof option.label === 'string')) return false;
  if (!bundle.localizedMetadata.every((metadata, index) =>
    metadata &&
    metadata.locale === locales[index] &&
    typeof metadata.title === 'string' &&
    typeof metadata.description === 'string' &&
    typeof metadata.certificateMessage === 'string' &&
    typeof metadata.completedMessage === 'string')) return false;
  return bundle.lessonRefs.every((reference) =>
    reference &&
    typeof reference.id === 'string' &&
    reference.href === `/courses/${reference.id}/current/`);
}

/** A deployed course `data/course.json` is either an embedded course (legacy) or a pointer. */
export type ExportedCoursePayload = ExportedCourse | CoursePointer;

/** True when a parsed course payload is the referenced-lesson pointer shape. */
export function isCoursePointer(
  payload: ExportedCoursePayload | null | undefined
): payload is CoursePointer {
  return (
    !!payload &&
    (payload as CoursePointer).kind === 'course-pointer' &&
    Array.isArray((payload as CoursePointer).lessonRefs)
  );
}

/**
 * Validates an unknown fetched `data/course.json` as either an embedded course
 * (legacy — `lessons`/`blocks` inline) or a course pointer (`kind` +
 * `lessonRefs`). Used by the export player to accept both shapes and to reject
 * SPA/HTML fallbacks served with a 200. A missing locale variant returning an
 * HTML shell fails this because it has no `id`/`lessonRefs`/`blocks`.
 */
export function isValidExportedCoursePayload(
  data: unknown
): data is ExportedCoursePayload {
  const parsed = parseExportedCoursePayload(data);
  return parsed !== null && parsed === data;
}

/**
 * Parses fetched course JSON while treating malformed optional narration as
 * unavailable. Core course/pointer shape failures still return null so HTML
 * fallbacks and incomplete packages cannot enter the player.
 */
export function parseExportedCoursePayload(data: unknown): ExportedCoursePayload | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  if (typeof d.id !== 'string') return null;
  if (d.kind === 'course-pointer') {
    return Array.isArray(d.lessonRefs) ? data as CoursePointer : null;
  }
  if (!Array.isArray(d.lessons) || !Array.isArray(d.blocks)) return null;
  if (d.narration === undefined || isValidNarrationManifest(d.narration)) {
    return data as ExportedCourse;
  }

  const courseWithoutNarration = { ...d };
  delete courseWithoutNarration.narration;
  return courseWithoutNarration as unknown as ExportedCourse;
}

/** Runtime validator for optional narration data loaded from APIs/static JSON. */
export function isValidNarrationManifest(data: unknown): data is NarrationManifest {
  if (!data || typeof data !== 'object') return false;
  const manifest = data as Record<string, unknown>;
  const blocks = manifest.blocks;
  if (!blocks || typeof blocks !== 'object' || Array.isArray(blocks)) return false;

  const isValidEntry = (value: unknown): value is NarrationManifestEntry => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const entry = value as Record<string, unknown>;
    if (entry.status === 'ready') {
      return typeof entry.url === 'string' && entry.url.trim().length > 0;
    }
    if (entry.status === 'empty' || entry.status === 'missing') {
      return !('url' in entry);
    }
    return false;
  };
  const isValidEntryMap = (
    value: unknown,
    requireEntry = false
  ): value is Record<string, NarrationManifestEntry> => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const entries = Object.values(value);
    return (!requireEntry || entries.length > 0) && entries.every(isValidEntry);
  };

  if (!Object.values(blocks).every((block) => isValidEntryMap(block, true))) return false;
  if (manifest.lessonTitles !== undefined && !isValidEntryMap(manifest.lessonTitles)) return false;
  if (manifest.mapRegions !== undefined && !isValidEntryMap(manifest.mapRegions)) return false;
  if (manifest.staticPrompts !== undefined) {
    if (!isValidEntryMap(manifest.staticPrompts)) return false;
    const promptKeys = new Set<string>([
      ...STATIC_NARRATION_PROMPT_KEYS,
      ...RETIRED_STATIC_NARRATION_PROMPT_KEYS,
    ]);
    if (!Object.keys(manifest.staticPrompts).every((key) => promptKeys.has(key))) return false;
  }
  return true;
}

function mapRegionIdsForBlocks(blocks: readonly CourseBlock[]): Set<string> {
  const regionIds = new Set<string>();
  for (const block of blocks) {
    if (block.type !== 'map') continue;
    for (const region of block.resolvedMapVersion?.regions ?? []) {
      regionIds.add(String(region.id));
    }
  }
  return regionIds;
}

function narrationEntriesEqual(
  left: NarrationManifestEntry,
  right: NarrationManifestEntry
): boolean {
  return left.status === right.status
    && (left.status !== 'ready' || (right.status === 'ready' && left.url === right.url));
}

function mergeNarrationEntry(
  current: NarrationManifestEntry | undefined,
  incoming: NarrationManifestEntry
): NarrationManifestEntry {
  if (!current) return incoming;
  return narrationEntriesEqual(current, incoming) ? current : { status: 'missing' };
}

export interface NarrationManifestContribution {
  lessons: readonly Pick<Lesson, 'id'>[];
  blocks: readonly CourseBlock[];
  narration?: NarrationManifest;
}

/**
 * Merge narration from one or more lesson payloads while retaining only entries
 * owned by the included blocks. Shared region and prompt conflicts fail closed.
 */
export function mergeNarrationManifests(
  contributions: readonly NarrationManifestContribution[]
): NarrationManifest | undefined {
  const blocks: NarrationManifest['blocks'] = {};
  const lessonTitles: NonNullable<NarrationManifest['lessonTitles']> = {};
  const mapRegions: NonNullable<NarrationManifest['mapRegions']> = {};
  const staticPrompts: NonNullable<NarrationManifest['staticPrompts']> = {};
  let hasManifest = false;

  for (const { lessons: includedLessons, blocks: includedBlocks, narration } of contributions) {
    if (!narration) continue;
    hasManifest = true;

    for (const lesson of includedLessons) {
      const entry = narration.lessonTitles?.[lesson.id];
      if (entry) lessonTitles[lesson.id] = mergeNarrationEntry(lessonTitles[lesson.id], entry);
    }

    for (const block of includedBlocks) {
      const entry = narration.blocks[block.id];
      if (entry) blocks[block.id] = entry;
    }

    for (const regionId of mapRegionIdsForBlocks(includedBlocks)) {
      const entry = narration.mapRegions?.[regionId];
      if (entry) mapRegions[regionId] = mergeNarrationEntry(mapRegions[regionId], entry);
    }

    for (const key of STATIC_NARRATION_PROMPT_KEYS) {
      const entry = narration.staticPrompts?.[key];
      if (entry) staticPrompts[key] = mergeNarrationEntry(staticPrompts[key], entry);
    }
  }

  if (!hasManifest) return undefined;
  return {
    blocks,
    ...(Object.keys(lessonTitles).length > 0 ? { lessonTitles } : {}),
    ...(Object.keys(mapRegions).length > 0 ? { mapRegions } : {}),
    ...(Object.keys(staticPrompts).length > 0 ? { staticPrompts } : {}),
  };
}

/**
 * Assemble a course pointer and its fetched lesson packages into a single
 * `ExportedCourse`, preserving the pointer's ref order. Each lesson package is a
 * one-lesson `ExportedCourse` (see `lessonStateToExportedCourse`); their lesson
 * metadata and blocks are concatenated in the order given. Course-level fields
 * come from the pointer. Callers MUST pass `lessonPackages` in `lessonRefs`
 * order (e.g. the aligned result of a `Promise.all` over the refs). Pure — no I/O.
 */
export function stitchPointerCourse(
  pointer: CoursePointer,
  lessonPackages: ExportedCourse[]
): ExportedCourse {
  const lessons: Lesson[] = [];
  const blocks: CourseBlock[] = [];
  for (const pkg of lessonPackages) {
    for (const lesson of pkg.lessons) lessons.push(lesson);
    for (const block of pkg.blocks) blocks.push(block);
  }
  const narration = mergeNarrationManifests(lessonPackages.map((pkg) => ({
    lessons: pkg.lessons,
    blocks: pkg.blocks,
    narration: pkg.narration,
  })));
  return {
    id: pointer.id,
    numericId: pointer.numericId,
    title: pointer.title,
    description: pointer.description,
    locale: pointer.locale,
    defaultLocale: pointer.defaultLocale,
    availableLocales: pointer.availableLocales,
    lessons,
    blocks,
    ...(narration ? { narration } : {}),
    completionCertificateEnabled: pointer.completionCertificateEnabled,
    certificateMessage: pointer.certificateMessage,
    completedMessage: pointer.completedMessage,
    timedCourse: pointer.timedCourse,
    durationMinutes: pointer.durationMinutes,
    minimumRequiredMinutes: pointer.minimumRequiredMinutes,
  };
}
